/**
 * @description Utility function to check if an error is a network error.
 * This includes checking for offline status, Axios network errors,
 * VPN connection issues, and specific HTTP status codes that might indicate a temporary issue.
 *
 * @param {unknown} error - The error object to check.
 * @returns {boolean} - Returns true if the error is a network error, otherwise false.
 */
export const checkNetworkError = (error: unknown): boolean => {
  // Check if the browser is offline
  if (navigator?.onLine === false) {
    return true;
  }

  // Type guard for error object
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errorObj = error as Record<string, any>;

  // Common network error codes
  const networkErrorCodes = [
    'ERR_NETWORK',
    'ERR_ADDRESS_UNREACHABLE',
    'ERR_NETWORK_CHANGED',
    'ERR_NETWORK_IO_SUSPENDED',
    'ERR_INTERNET_DISCONNECTED',
    'ERR_CONNECTION_REFUSED',
    'ERR_CONNECTION_TIMED_OUT',
    'ERR_CONNECTION_RESET',
    'ENOTFOUND',
    'ECONNRESET',
    'ECONNREFUSED',
    'ETIMEDOUT',
  ];

  // Check error code
  if (errorObj?.code && networkErrorCodes.includes(errorObj.code)) {
    return true;
  }

  // Check error message for network-related keywords
  const errorMessage = errorObj?.message?.toLowerCase() || '';
  const networkKeywords = [
    'network error',
    'network request failed',
    'connection refused',
    'connection timed out',
    'connection reset',
    'address unreachable',
    'network changed',
    'network io suspended',
    'internet disconnected',
    'fetch failed',
  ];

  if (networkKeywords.some((keyword) => errorMessage.includes(keyword))) {
    return true;
  }

  // Handle specific HTTP status codes that might indicate a temporary network issue
  const networkStatusCodes = [0, 408, 429, 502, 503, 504, 522, 524];
  if (
    errorObj?.response?.status !== undefined &&
    networkStatusCodes.includes(errorObj.response.status)
  ) {
    return true;
  }

  // Handle 403 status as potential auth/session issue (not necessarily logout)
  if (errorObj?.response?.status === 403 || errorObj?.message === '403') {
    return true;
  }

  // Fetch API network error (e.g., when the request is aborted)
  if (errorObj?.name === 'AbortError' || errorObj?.name === 'TypeError') {
    return true;
  }

  // Check if error is related to CORS (might happen during network transitions)
  if (errorMessage.includes('cors') || errorMessage.includes('cross-origin')) {
    return true;
  }

  return false;
};
